/**
 * @fileoverview Client-side document preview data transform — a thin Dexie-shape adapter over the
 * shared @firesquirrel/document-render core. Merge-field resolution, section gating, violation
 * grouping, and DocumentRenderData assembly are NOT duplicated here (G-DRY); they are imported from
 * the shared core and used verbatim by the worker PDF path too, so preview and PDF cannot drift.
 * @module core-domain/reporting/documentFieldBindings
 *
 * Responsibilities: - Re-export shared config/resolved types for backward compatibility.
 *                   - Define PreviewDataSource / PreviewDepartmentSource (frontend-only shapes).
 *                   - Provide SAMPLE_PREVIEW_DATA (system-web, no real inspections).
 *                   - Extract Dexie/sample-shaped violations, photos, and signatures, then delegate
 *                     all assembly + gating to assembleDocumentRenderData.
 * Dependencies:     - @firesquirrel/document-render (shared types + assembleDocumentRenderData core)
 *                   - ./advancedBindingResolver
 * Architecture:     - Pure logic — no database calls, no side effects. Works with plain
 *                     JS objects from Dexie or sample data.
 * Notes:            - Complex-visit fields are projected only from an explicit data-source value;
 *                     sample complex data is restricted to the design-time preview preset.
 *                   - The genuine source difference from the worker is signature extraction (event
 *                     inline objects here vs canonical event.signatures[] in the worker) and the
 *                     loose Dexie violation/photo shapes; everything else is shared.
 */

import { resolveAllAdvancedBindings } from './advancedBindingResolver';
import {
  resolveDepartmentLogoUrl,
  resolveDepartmentSecondLogoUrl,
  type BrandingLogoRefLike,
} from './brandingLogo';

// Re-export all shared types so existing consumers importing from this module still compile.
export type {
  DocumentConfig, PageSettings, SectionDef,
  HeaderConfig, RecipientBlockConfig, SubjectLineConfig, SalutationConfig,
  BodyTextConfig, BodyParagraph, MetadataConfig, ViolationConfig, PhotoConfig,
  CommentsConfig, ContactBlockConfig, SignaturesConfig, LegalTextConfig,
  FooterConfig, StaticContentConfig,
  ResolvedHeader, ResolvedRecipientBlock, ResolvedSubjectLine, ResolvedSalutation,
  ResolvedBodyParagraph, ResolvedMetadataField, ResolvedViolationItem, ResolvedViolationGroup,
  ResolvedPhoto, ResolvedComments, ResolvedContactBlock, ResolvedSignature, ResolvedSignatures,
  ResolvedLegalText, ResolvedFooter, ResolvedStaticContent,
  ResolvedFormResponseField, ResolvedFormSection, ResolvedFormResponseGroup,
  AdvancedDocumentMode, AdvancedBinding, AdvancedFieldRenderMode, AdvancedFieldSourceType,
  AdvancedFieldFormatting, AdvancedFieldPlacement, AdvancedFieldResolvedFrom,
  ResolvedAdvancedField, DocumentRenderData,
} from '@firesquirrel/document-render';

// Import the types used by the adapter logic below (the source-specific extraction + delegation).
import type {
  DocumentConfig, ResolvedViolationItem, ResolvedPhoto,
  ResolvedSignature, ResolvedSignatures, ResolvedAdvancedField,
  AdvancedBinding, AdvancedFieldPlacement, DocumentRenderData,
} from '@firesquirrel/document-render';

// ---------------------------------------------------------------------------
// Preview Data Source — plain objects from Dexie or sample data (frontend-only)
// ---------------------------------------------------------------------------

/**
 * @contract PreviewDataSource
 * Purpose: Plain local or design-time inputs for the shared document preview assembler.
 * Fields: Entity, series, event, inspector, department, violations, photos, optional bindings,
 *   and optional complex-visit render projections.
 * Rules: Real complex-visit projections are included only when the caller supplies them.
 * Sync: Read-only preview input; no persistence.
 */
export interface PreviewDataSource {
  entity: Record<string, any>;
  series: Record<string, any>;
  event?: Record<string, any>;
  inspector: { name: string; email: string; title?: string; badgeNumber?: string };
  department: Record<string, any>;
  violations: Record<string, any>[];
  photos: Record<string, any>[];
  complexVisit?: {
    summary: NonNullable<DocumentRenderData['complexVisitSummary']>;
    participants: Array<Omit<NonNullable<DocumentRenderData['complexParticipants']>[number], 'data'>>;
    standaloneSummary: NonNullable<DocumentRenderData['complexStandaloneSummary']>;
  };
  advanced?: {
    templateSnapshot?: {
      forms: Array<{
        formId: string;
        formName: string;
        fields: Array<{ fieldId: string; type: string; label: string; defaultValue?: any; [k: string]: any }>;
      }>;
    };
    responses?: Record<string, any>;
  };
}

// ---------------------------------------------------------------------------
// Sample data for system-web (no real inspections available)
// ---------------------------------------------------------------------------

const SAMPLE_DEPARTMENT_LOGO =
  'data:image/svg+xml;charset=UTF-8,' +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" width="320" height="96" viewBox="0 0 320 96">' +
      '<defs><linearGradient id="g" x1="0" x2="1" y1="0" y2="1"><stop offset="0%" stop-color="#ffb347"/><stop offset="100%" stop-color="#f97316"/></linearGradient></defs>' +
      '<rect width="320" height="96" rx="20" fill="url(#g)"/>' +
      '<circle cx="54" cy="48" r="24" fill="rgba(255,255,255,0.2)"/>' +
      '<path d="M54 26c7 9 10 15 10 22 0 8-5 14-10 14s-10-6-10-14c0-7 3-13 10-22Z" fill="#fff"/>' +
      '<text x="94" y="43" font-family="Arial, Helvetica, sans-serif" font-size="22" font-weight="700" fill="#fff">Sample Fire Dept</text>' +
      '<text x="94" y="66" font-family="Arial, Helvetica, sans-serif" font-size="12" font-weight="600" fill="#fff" opacity="0.92">Preview branding and sample data</text>' +
    '</svg>'
  );

// Sample inspection photo placeholders — SVG stand-ins used only in SAMPLE_PREVIEW_DATA so the
// WYSIWYG document preview shows real images instead of broken-link icons.
const SAMPLE_PHOTO_VEGETATION =
  'data:image/svg+xml;charset=UTF-8,' +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" width="320" height="240" viewBox="0 0 320 240">' +
      '<rect width="320" height="240" fill="#7EC8E3"/>' +
      '<rect y="170" width="320" height="70" fill="#8B7355"/>' +
      '<ellipse cx="160" cy="170" rx="190" ry="75" fill="#5D8A3C"/>' +
      '<ellipse cx="65" cy="158" rx="60" ry="48" fill="#4A7A2C"/>' +
      '<ellipse cx="258" cy="163" rx="70" ry="52" fill="#6B9E48"/>' +
      '<ellipse cx="160" cy="148" rx="85" ry="58" fill="#3D6B24"/>' +
      '<rect x="52" y="108" width="9" height="55" fill="#5C3D11"/>' +
      '<ellipse cx="57" cy="100" rx="20" ry="26" fill="#2D5A1B"/>' +
      '<rect x="252" y="98" width="9" height="62" fill="#5C3D11"/>' +
      '<ellipse cx="257" cy="90" rx="22" ry="28" fill="#2D5A1B"/>' +
      '<rect x="140" y="120" width="8" height="48" fill="#5C3D11"/>' +
      '<ellipse cx="144" cy="112" rx="18" ry="22" fill="#4A7A2C"/>' +
    '</svg>'
  );

const SAMPLE_PHOTO_DECK =
  'data:image/svg+xml;charset=UTF-8,' +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" width="320" height="240" viewBox="0 0 320 240">' +
      '<rect width="320" height="240" fill="#87CEEB"/>' +
      '<rect y="195" width="320" height="45" fill="#7A6244"/>' +
      '<rect x="55" y="92" width="210" height="128" fill="#D4A574"/>' +
      '<polygon points="160,32 20,98 300,98" fill="#8B3A3A"/>' +
      '<rect x="88" y="120" width="34" height="44" fill="#F5DEB3"/>' +
      '<rect x="198" y="120" width="34" height="44" fill="#F5DEB3"/>' +
      '<rect x="133" y="152" width="54" height="68" fill="#6B4226"/>' +
      '<rect x="55" y="188" width="210" height="28" fill="#C8965A"/>' +
      '<line x1="78" y1="188" x2="78" y2="216" stroke="#A07840" stroke-width="3"/>' +
      '<line x1="108" y1="188" x2="108" y2="216" stroke="#A07840" stroke-width="3"/>' +
      '<line x1="138" y1="188" x2="138" y2="216" stroke="#A07840" stroke-width="3"/>' +
      '<line x1="182" y1="188" x2="182" y2="216" stroke="#A07840" stroke-width="3"/>' +
      '<line x1="212" y1="188" x2="212" y2="216" stroke="#A07840" stroke-width="3"/>' +
      '<line x1="242" y1="188" x2="242" y2="216" stroke="#A07840" stroke-width="3"/>' +
      '<circle cx="92" cy="193" r="20" fill="#4A7A2C"/>' +
      '<circle cx="228" cy="196" r="17" fill="#5D8A3C"/>' +
      '<circle cx="68" cy="205" r="12" fill="#3D6B24"/>' +
    '</svg>'
  );

const SAMPLE_COMPLEX_VISIT: NonNullable<PreviewDataSource['complexVisit']> = {
  summary: {
    complexName: 'Sample Apartment Complex',
    anchorName: 'Leasing Office',
    visitDate: '2026-03-10',
    governedCount: 2,
    standaloneCount: 1,
  },
  participants: [
    {
      caseId: 'PREVIEW-ROOT',
      eventIndex: 0,
      isRoot: true,
      entityName: 'Leasing Office',
      entityAddress: '100 Sample Court, Lake City, NV 89451',
      entityCategory: 'building',
      entityStatus: 'active',
      participationMode: 'complex-governed',
    },
    {
      caseId: 'PREVIEW-MEMBER',
      eventIndex: 0,
      isRoot: false,
      entityName: 'Building A',
      entityAddress: '110 Sample Court, Lake City, NV 89451',
      entityCategory: 'building',
      entityStatus: 'active',
      participationMode: 'complex-governed',
    },
  ],
  standaloneSummary: [{ entityName: 'Maintenance Building', status: 'scheduled' }],
};

export const SAMPLE_PREVIEW_DATA: PreviewDataSource = {
  entity: {
    name: 'Smith Residence',
    entityAddress: '123 Pine Street, Lake City, NV 89451',
    entityCategory: 'residential',
    parcelNumber: '054-230-015',
    parcelDetails: { ownerName: 'SMITH, JOHN' },
    entityLocation: {
      address: {
        fullAddress: '123 Pine Street, Lake City, NV 89451',
        street: '123 Pine Street',
        city: 'Lake City',
        state: 'NV',
        zip: '89451',
      },
    },
    primaryContact: {
      phone: '(555) 123-4567',
      email: 'jsmith@email.com',
    },
    contacts: [
      { name: 'John Smith', phoneNumbers: [{ number: '(555) 123-4567' }], emails: [{ email: 'jsmith@email.com' }] },
    ],
  },
  series: {
    _id: 'CASE-2026-0042',
    seriesType: { name: 'Defensible Space' },
    caseType: { name: 'Defensible Space Inspection' },
    wildlandData: {
      slope: 'Moderate (20-40%)',
      acreageEligible: 'Yes',
      curbsideVacant: 'No',
      insuranceSolicited: 'No',
      complaintConstruction: 'Complaint',
      treeRemoval: '',
      permitNumber: '',
    },
    externalNotes: 'Vegetation clearance needed on north slope. Owner was cooperative and plans to address violations within 30 days.',
    createdAt: '2026-03-01T10:00:00Z',
    events: [
      { photos: [{ blobUrl: SAMPLE_PHOTO_VEGETATION }] },
    ],
  },
  event: {
    eventIndex: 0,
    eventType: { name: 'Initial Inspection', code: 'initial-inspection' },
    status: 'completed',
    completedAt: '2026-03-10T14:30:00Z',
    startedAt: '2026-03-10T09:00:00Z',
    externalNotes: '',
    inspectorSignature: {
      signerName: 'Bob Martinez',
      signedAt: '2026-03-10T14:30:00Z',
    },
  },
  inspector: {
    name: 'Bob Martinez',
    email: 'bmartinez@nltfpd.net',
    title: 'Fire Prevention Inspector II',
    badgeNumber: 'FPI-2847',
  },
  department: {
    name: 'Sample Fire Department',
    phone: '(555) 555-0100',
    fax: '(555) 555-0101',
    email: 'preview@samplefd.local',
    website: 'samplefd.local',
    address: {
      fullAddress: '100 Preview Plaza, Sample City, AZ 85001',
      street: '100 Preview Plaza',
      city: 'Sample City',
      state: 'AZ',
      zip: '85001',
    },
    branding: {
      logo: { blobUrl: SAMPLE_DEPARTMENT_LOGO },
      secondLogo: { blobUrl: SAMPLE_DEPARTMENT_LOGO },
    },
  },
  violations: [
    { code: 'A', id: 'vio-a', description: 'Remove flammable vegetation and items that could catch fire within 5 feet of any portion of the structure, and beneath combustible decks, balconies, and stairs, pursuant to NLTFPD RES 18-2 & NLTFPD SOG #700.001', zone: 'Non-Combustible Zone (0-5ft)', codeReference: 'IWUIC 604.2 & NLTFPD SOG #700.001', severity: { name: 'High' }, status: { name: 'Open' }, source: 'question', fieldId: 'q-defensible-space', questionText: 'Is defensible space maintained around the structure?', occurrences: [{ id: 'occ-a-1', zone: 'Non-Combustible Zone (0-5ft)', location: 'North wall, under deck' }, { id: 'occ-a-2', zone: 'Non-Combustible Zone (0-5ft)', location: 'East side, beneath stairs' }] },
    { code: 'D', description: 'Remove all dead and dying grass, plants, shrubs, trees, branches, leaves, weeds and needles, pursuant to IWUIC 604.3 & NLTFPD SOG #700.001', zone: 'Lean Clean Green Zone (5-30ft)', codeReference: 'IWUIC 604.3 & NLTFPD SOG #700.001', severity: { name: 'High' }, status: { name: 'Corrected' }, source: 'question', fieldId: 'q-dead-vegetation', questionText: 'Is the property clear of dead and dying vegetation?', occurrences: [{ zone: 'Lean Clean Green Zone (5-30ft)', location: 'Rear slope' }] },
    { code: 'H', description: 'Cut annual grasses and plants down to a maximum height of 4 inches, pursuant to IWUIC 604.2 & NLTFPD SOG #700.001', zone: 'Wildland Fuel Reduction Zone (30-100ft)', codeReference: 'IWUIC 604.2 & NLTFPD SOG #700.001', severity: { name: 'Medium' }, status: { name: 'Open' }, source: 'question', fieldId: 'q-dead-vegetation', questionText: 'Is the property clear of dead and dying vegetation?', occurrences: [{ zone: 'Wildland Fuel Reduction Zone (30-100ft)', location: 'Front field' }] },
    { code: 'V', description: 'Remove inoperable or abandoned vehicle from the property, pursuant to local nuisance ordinance', zone: 'Property-wide', codeReference: 'Muni Code 8.12', severity: { name: 'Medium' }, status: { name: 'Open' }, source: 'additional', occurrences: [{ location: 'Side driveway' }] },
  ],
  photos: [
    // occurrence-scoped photo (linked to violation A's first occurrence): with the "inline with
    // violations" layout this renders beneath violation A instead of the flat gallery (DFE-005).
    { blobUrl: SAMPLE_PHOTO_DECK, caption: 'Deck area clearance needed', takenAt: '2026-03-10T10:22:00Z', occurrenceId: 'occ-a-1', violationId: 'vio-a' },
    // general (unlinked) photo: stays in the standalone gallery under every layout.
    { blobUrl: SAMPLE_PHOTO_VEGETATION, caption: 'North slope vegetation', takenAt: '2026-03-10T10:15:00Z' },
  ],
  complexVisit: SAMPLE_COMPLEX_VISIT,
};

// ---------------------------------------------------------------------------
// Department → PreviewDataSource (shared by system-web and tenant-web)
// ---------------------------------------------------------------------------

/**
 * A tenant (department) document as projected into local-first Dexie — `tenants`
 * in system-web and `system_tenant_ref` in tenant-web are projections of the same
 * system tenant document, so a single shape and mapper serves both apps.
 */
export interface PreviewDepartmentSource {
  _id?: unknown;
  name?: string;
  phone?: string;
  website?: string;
  status?: string;
  address?: { street1?: string; street2?: string; city?: string; state?: string; zipCode?: string };
  /**
   * Department branding projected into the preview. Carries both the uploaded logo refs
   * (`logo` / `secondLogo`, written by the BrandingPage) and the legacy `logoUrl` string.
   * The canonical resolver (`resolveDepartmentLogoUrl`) collapses these into one effective
   * URL so the preview matches the worker-rendered PDF regardless of which field the
   * department used.
   */
  branding?: {
    logo?: BrandingLogoRefLike;
    secondLogo?: BrandingLogoRefLike;
    logoUrl?: string;
    brandColors?: { primary?: string; secondary?: string };
  };
}

function hasPreviewDepartmentData(department: PreviewDepartmentSource): boolean {
  const address = department.address;
  return Boolean(
    department.name?.trim() ||
    department.phone?.trim() ||
    department.website?.trim() ||
    address?.street1?.trim() ||
    address?.street2?.trim() ||
    address?.city?.trim() ||
    address?.state?.trim() ||
    address?.zipCode?.trim() ||
    // An uploaded logo (either slot) or a legacy logoUrl all count as "has data" so a
    // department that only set a logo still drives the preview instead of falling back to
    // SAMPLE_PREVIEW_DATA.
    resolveDepartmentLogoUrl(department.branding) ||
    resolveDepartmentSecondLogoUrl(department.branding) ||
    department.branding?.brandColors,
  );
}

/**
 * Map a tenant (department) record to the `PreviewDataSource.department` shape the
 * shared document renderer expects (address.{street,city,state,zip}, branding.logo.blobUrl,
 * branding.secondLogo.blobUrl, branding.brandColors).
 *
 * Logo resolution delegates to the canonical `resolveDepartmentLogoUrl` /
 * `resolveDepartmentSecondLogoUrl` helpers so the preview receives the same effective logo
 * URL the worker-rendered PDF uses — whether the department set the new uploaded `logo` ref
 * or the legacy `logoUrl` string. brandColors is forwarded so the preview renders the same
 * department colors the worker uses for the PDF (Finding 2 parity).
 */
export function tenantToPreviewDepartment(t: PreviewDepartmentSource): Record<string, any> {
  const a = t.address;
  const fullAddress = a
    ? [a.street1, a.street2, a.city, a.state, a.zipCode].filter(Boolean).join(', ')
    : undefined;
  const logoUrl = resolveDepartmentLogoUrl(t.branding);
  const secondLogoUrl = resolveDepartmentSecondLogoUrl(t.branding);
  const brandColors = t.branding?.brandColors;
  // Carry uploadedFileId through so host pages can resolve SAS URLs for display.
  // blobUrl alone is a private Azure Blob Storage URL that requires a SAS token
  // to render in an <img> tag; the host page calls getUploadedFileDownloadUrl
  // with uploadedFileId to get a short-lived displayable URL.
  const logoRef = t.branding?.logo;
  const secondLogoRef = t.branding?.secondLogo;
  const branding = (logoUrl || secondLogoUrl || brandColors)
    ? {
        ...(logoUrl ? { logo: { blobUrl: logoUrl, uploadedFileId: logoRef?.uploadedFileId } } : {}),
        ...(secondLogoUrl ? { secondLogo: { blobUrl: secondLogoUrl, uploadedFileId: secondLogoRef?.uploadedFileId } } : {}),
        ...(brandColors ? { brandColors } : {}),
      }
    : undefined;
  return {
    name: t.name,
    phone: t.phone,
    website: t.website,
    address: a
      ? { street: a.street1, city: a.city, state: a.state, zip: a.zipCode, fullAddress }
      : undefined,
    branding,
  };
}

/**
 * Build the preview data source for the document builder. A real department's
 * branding/contact data overrides the sample department; when no department is
 * available or the department payload is effectively empty, the full
 * SAMPLE_PREVIEW_DATA is returned so the preview always renders.
 *
 * This is the single shared entry point used by both system-web (department picked
 * from the active tenant list) and tenant-web (the implicit current department).
 */
export function buildPreviewDataSource(department: PreviewDepartmentSource | null | undefined): PreviewDataSource {
  if (!department || !hasPreviewDepartmentData(department)) return SAMPLE_PREVIEW_DATA;
  return { ...SAMPLE_PREVIEW_DATA, department: tenantToPreviewDepartment(department) };
}

/**
 * @process resolvePreviewDepartmentLogoUrl
 * Steps:           Read the effective department logo URL from a built PreviewDataSource.
 *                  `tenantToPreviewDepartment` already collapsed the uploaded-ref / legacy-
 *                  string precedence into `department.branding.logo.blobUrl`, so this is a
 *                  trivial read — but centralising it here means both host pages (tenant-web
 *                  and system-web) call one function instead of each inlining their own
 *                  partial copy (DRY).
 * Inputs:          dataSource — a PreviewDataSource produced by `buildPreviewDataSource`.
 * Outputs:         the department logo URL/blob-name string, or undefined when the department
 *                  has no logo (e.g. the sample department).
 * Failure Handling:never throws; missing branding collapses to undefined.
 * Idempotency:     REQUIRED — pure function of its input.
 */
export function resolvePreviewDepartmentLogoUrl(
  dataSource: PreviewDataSource,
): string | undefined {
  return (dataSource.department as { branding?: { logo?: { blobUrl?: string } } })
    ?.branding?.logo?.blobUrl || undefined;
}

/**
 * @process resolvePreviewDepartmentSecondLogoUrl
 * Steps:           Read the secondary logo blobUrl from the preview data source's
 *                  department branding. Returns undefined when the department has
 *                  no secondary logo (e.g. the sample department).
 * Inputs:          preview data source (built by buildPreviewDataSource).
 * Outputs:         a logo URL/blob-name string, or undefined.
 * Failure Handling:never throws; missing branding collapses to undefined.
 * Idempotency:     REQUIRED — pure function of its input.
 */
export function resolvePreviewDepartmentSecondLogoUrl(
  dataSource: PreviewDataSource,
): string | undefined {
  return (dataSource.department as { branding?: { secondLogo?: { blobUrl?: string } } })
    ?.branding?.secondLogo?.blobUrl || undefined;
}

/**
 * @process resolvePreviewDepartmentLogoFileId
 * Steps:           Read the primary logo uploadedFileId from the preview data
 *                  source's department branding. Host pages use this to call
 *                  getUploadedFileDownloadUrl for SAS URL resolution.
 * Inputs:          preview data source.
 * Outputs:         uploadedFileId string, or undefined.
 * Idempotency:     REQUIRED — pure function of its input.
 */
export function resolvePreviewDepartmentLogoFileId(
  dataSource: PreviewDataSource,
): string | undefined {
  return (dataSource.department as { branding?: { logo?: { uploadedFileId?: string } } })
    ?.branding?.logo?.uploadedFileId || undefined;
}

/**
 * @process resolvePreviewDepartmentSecondLogoFileId
 * Steps:           Read the secondary logo uploadedFileId from the preview data
 *                  source's department branding.
 * Inputs:          preview data source.
 * Outputs:         uploadedFileId string, or undefined.
 * Idempotency:     REQUIRED — pure function of its input.
 */
export function resolvePreviewDepartmentSecondLogoFileId(
  dataSource: PreviewDataSource,
): string | undefined {
  return (dataSource.department as { branding?: { secondLogo?: { uploadedFileId?: string } } })
    ?.branding?.secondLogo?.uploadedFileId || undefined;
}

/**
 * @process collectDepartmentLogoIds
 * Steps:           Extract department logo uploadedFileIds from a PreviewDataSource. Same
 *                  pattern as collectUploadedLogoIds (document-render) but for department
 *                  branding. Returns the IDs to pass to useResolvedLogoUrls (file-service-client).
 * Inputs:          PreviewDataSource (from buildPreviewDataSource).
 * Outputs:         De-duplicated array of uploadedFileId strings (0–2 entries).
 * Failure Handling: never throws; returns [] when branding is absent.
 * Idempotency:     REQUIRED — pure function of its input.
 */
export function collectDepartmentLogoIds(dataSource: PreviewDataSource): string[] {
  const dept = dataSource.department as Record<string, any>;
  const ids: string[] = [];
  const logoId = dept?.branding?.logo?.uploadedFileId;
  const secondId = dept?.branding?.secondLogo?.uploadedFileId;
  if (typeof logoId === 'string' && logoId.length > 0) ids.push(logoId);
  if (typeof secondId === 'string' && secondId.length > 0 && secondId !== logoId) ids.push(secondId);
  return ids;
}

/**
 * @process buildBrandedPreviewDataSource
 * Steps:           Patch the department branding inside a PreviewDataSource so that raw Azure
 *                  blob URLs (private, require SAS authentication) are replaced with resolved
 *                  displayable URLs. This is the single DRY point where department logo URL
 *                  patching occurs — all host pages (tenant-web builder, system-web builder,
 *                  detail panel preview) delegate here instead of inlining their own patching.
 * Inputs:          previewDataSource (from buildPreviewDataSource), resolvedLogoUrls (map of
 *                  uploadedFileId → SAS URL, populated by the host page's async resolver).
 * Outputs:         A new PreviewDataSource with department.branding.logo.blobUrl and
 *                  secondLogo.blobUrl replaced where resolved URLs are available. Returns
 *                  the input unchanged when no logos can be resolved.
 * Failure Handling: never throws; empty map or missing branding short-circuits to the input.
 * Idempotency:     REQUIRED — pure function of its inputs.
 */
export function buildBrandedPreviewDataSource(
  previewDataSource: PreviewDataSource,
  resolvedLogoUrls: Record<string, string>,
): PreviewDataSource {
  if (Object.keys(resolvedLogoUrls).length === 0) return previewDataSource;
  const dept = previewDataSource.department as Record<string, any>;
  if (!dept?.branding) return previewDataSource;
  const logo = dept.branding?.logo;
  const secondLogo = dept.branding?.secondLogo;
  const resolvedLogo = logo?.uploadedFileId ? resolvedLogoUrls[logo.uploadedFileId] : undefined;
  const resolvedSecond = secondLogo?.uploadedFileId ? resolvedLogoUrls[secondLogo.uploadedFileId] : undefined;
  if (!resolvedLogo && !resolvedSecond) return previewDataSource;
  return {
    ...previewDataSource,
    department: {
      ...dept,
      branding: {
        ...dept.branding,
        ...(resolvedLogo ? { logo: { ...logo, blobUrl: resolvedLogo } } : {}),
        ...(resolvedSecond ? { secondLogo: { ...secondLogo, blobUrl: resolvedSecond } } : {}),
      },
    },
  };
}

// ---------------------------------------------------------------------------
// Shared render-data core (G-DRY)
// ---------------------------------------------------------------------------
// Merge-field resolution, section gating, violation grouping, photo slicing, and the
// DocumentRenderData assembly all live in @firesquirrel/document-render and are shared
// verbatim with the worker PDF path. This module only performs the Dexie/sample-shape
// extraction that genuinely differs from the worker's canonical-Mongo extraction, then
// delegates to assembleDocumentRenderData — so the preview and the PDF cannot drift.

import {
  assembleDocumentRenderData,
  formatDocumentDate,
  isOccurrenceReportVisible,
  DEFAULT_DOCUMENT_BRANDING,
} from '@firesquirrel/document-render';
import type { MergeFieldContext, DocumentLayout } from '@firesquirrel/document-render';

// Re-export the shared merge-field helpers under their historical names so existing
// consumers importing them from this module keep compiling.
export { resolveMergeField, resolveAllMergeFields } from '@firesquirrel/document-render';

// ---------------------------------------------------------------------------
// Source-specific extraction (Dexie / sample data shapes)
// ---------------------------------------------------------------------------

/**
 * @process mapViolationItems
 * Steps:    Map loose Dexie/sample violation objects to the shared ResolvedViolationItem shape.
 *           Config-driven grouping is applied later by the shared groupViolations.
 * Inputs:   violations (plain objects from Dexie or sample data), config (DocumentConfig).
 * Outputs:  ResolvedViolationItem[] (ungrouped).
 * Failure:  Tolerant — missing fields collapse to ''.
 * Idempotency: REQUIRED — pure.
 */
function mapViolationItems(
  violations: Record<string, any>[],
  config: DocumentConfig,
): ResolvedViolationItem[] {
  const vc = config.violationConfig || { displayFormat: 'checklist' as const };
  return violations.map((v) => {
    // DFE-007 / G-503 (preview parity): exclude retracted/soft-deleted occurrences so the preview
    // matches the worker PDF, which filters them in extractViolations. Same shared predicate.
    const occ = (Array.isArray(v.occurrences) ? (v.occurrences as Record<string, any>[]) : [])
      .filter((o) => isOccurrenceReportVisible(o));
    return {
      code: v.code || v.violationCode || '',
      description: v.description || v.title || v.violationTitle || '',
      checked: true,
      codeReference: vc.showCodeReferences ? (v.codeReference || v.statuteReference || '') : undefined,
      location: vc.showLocationDescriptions ? (v.location || v.locationDescription || '') : undefined,
      zone: v.zone || v.zoneId || '',
      severity: v.severity?.name || v.severity?.code || '',
      status: v.status?.name || v.status?.code || v.resolutionStatus || '',
      // Question linkage + per-occurrence detail for the question-grouped layout (G-VIOL-QGROUP).
      // Carried for every groupBy mode; only consumed when groupBy === 'question'.
      questionId: v.questionId || v.fieldId || undefined,
      questionText: v.questionText || undefined,
      source: v.source === 'additional' ? 'additional' : (v.source === 'question' ? 'question' : undefined),
      // DFE-005 fallback: carry violation _id so distributeInlinePhotos can match by violationId
      // when occurrenceId is missing or unmatched (legacy data, sync gaps).
      _violationId: (v._id || v.id) ? String(v._id || v.id) : undefined,
      occurrences: occ.map((o) => ({
        id: (o._id || o.id) ? String(o._id || o.id) : undefined,
        zone: o.zone || o.zoneId,
        location: o.location || o.locationDescription,
        notes: o.notes,
        status: o.status?.name || o.status?.code || o.status,
        correctionStatus: o.correctionStatus,
        discoveredAt: o.discoveredAt ? formatDocumentDate(o.discoveredAt) : undefined,
        fixedAt: o.correctionCompletedAt ? formatDocumentDate(o.correctionCompletedAt) : o.fixedAt ? formatDocumentDate(o.fixedAt) : undefined,
        fixedNotes: o.correctionNotes || o.fixedNotes,
      })),
    };
  });
}

/**
 * @process mapPhotos
 * Steps:    Map loose Dexie/sample photo objects to the shared ResolvedPhoto shape. The shared
 *           assembler applies the `maxPhotos` slice, so this returns the full mapped set.
 * Inputs:   photos (plain objects), config (DocumentConfig).
 * Outputs:  ResolvedPhoto[] (unsliced).
 * Failure:  Tolerant — missing fields collapse to '' / undefined.
 * Idempotency: REQUIRED — pure.
 */
function mapPhotos(
  photos: Record<string, any>[],
  config: DocumentConfig,
): ResolvedPhoto[] {
  const pc = config.photoConfig || {};
  return photos.map((p) => ({
    blobUrl: p.blobUrl || '',
    caption: pc.showCaptions !== false ? (p.caption || '') : undefined,
    takenAt: p.takenAt ? formatDocumentDate(p.takenAt) : undefined,
    // Carry occurrence/violation linkage when present so the preview can place inline photos
    // identically to the worker PDF (DFE-005). String() coercion matches the worker's defensive fix.
    occurrenceId: p.occurrenceId ? String(p.occurrenceId) : undefined,
    violationId: p.violationId ? String(p.violationId) : undefined,
  }));
}

/**
 * @process resolveSignatures
 * Steps:    Resolve signature slots from the event's canonical signatures[] array (Mode C)
 *           with fallback to legacy inline fields (inspectorSignature / responsiblePartySignature)
 *           for backwards compatibility with pre-Mode C data. This brings the preview into parity
 *           with the worker's resolveSignatures in documentDataTransformer.ts.
 * Inputs:   config (DocumentConfig), ctx (MergeFieldContext — for event + inspector).
 * Outputs:  ResolvedSignatures.
 * Failure:  Tolerant — absent signature objects yield slots without urls/dates.
 * Idempotency: REQUIRED — pure.
 */
function resolveSignatures(
  config: DocumentConfig,
  ctx: MergeFieldContext,
): ResolvedSignatures {
  const s = config.signatures || { style: 'full' as const };
  const sigs: ResolvedSignature[] = [];

  // G-006: Read from canonical event.signatures[] (Mode C) first, then fall
  // back to legacy inline fields for pre-Mode C data.
  const canonicalSignatures: Array<Record<string, any>> =
    Array.isArray(ctx.event?.signatures) ? ctx.event!.signatures : [];

  const ROLE_LABELS: Record<string, string> = {
    inspector: 'Inspector',
    business: 'Responsible Party',
    witness: 'Witness',
    other: 'Other',
  };

  const enabledRoles: Array<{
    configFlag: boolean | undefined;
    canonicalRole: string;
    legacyField: string;
  }> = [
    { configFlag: s.inspectorSignature, canonicalRole: 'inspector', legacyField: 'inspectorSignature' },
    { configFlag: s.responsiblePartySignature, canonicalRole: 'business', legacyField: 'responsiblePartySignature' },
    { configFlag: s.witnessSignature, canonicalRole: 'witness', legacyField: '' },
  ];

  for (const { configFlag, canonicalRole, legacyField } of enabledRoles) {
    if (!configFlag) continue;
    // Prefer canonical Mode C match
    const matched = canonicalSignatures.find((sig) => sig.role === canonicalRole);
    // Fall back to legacy inline field for backwards compatibility
    const legacy = legacyField ? ctx.event?.[legacyField] : undefined;
    const blobUrl = matched?.attachmentRef?.blobName
      ? matched.attachmentRef.blobName
      : (matched?.blobUrl ?? legacy?.blobUrl);
    const signedAt = matched?.capturedAt ?? legacy?.signedAt;
    const name = matched?.signerName
      ?? (canonicalRole === 'inspector' ? ctx.inspector.name : legacy?.signerName);

    sigs.push({
      role: canonicalRole,
      label: ROLE_LABELS[canonicalRole] || canonicalRole,
      name,
      signatureUrl: blobUrl,
      signedAt: signedAt ? formatDocumentDate(signedAt) : undefined,
      showDate: s.dateFields !== false,
      showPrintedName: s.printedNameFields === true,
      showTitle: s.showTitle === true && canonicalRole === 'inspector',
      title: canonicalRole === 'inspector' ? ctx.inspector.title : undefined,
    });
  }

  return { style: s.style, signatures: sigs };
}

// ---------------------------------------------------------------------------
// Main Transform Function (thin adapter)
// ---------------------------------------------------------------------------

/**
 * @process projectComplexVisitPreviewData
 * Purpose: Project explicitly supplied complex-visit data into the assembled preview.
 * Steps: Return assembled data unchanged when no complex-visit source is present; otherwise
 *   copy the supplied summary and standalone rows and attach assembled render data to each
 *   supplied participant.
 * Inputs: Assembled render data and the optional complexVisit data-source projection.
 * Outputs: Render data with supplied summary, governed participants, and standalone summary,
 *   or the original render data when the source has no complex-visit projection.
 * Failure Handling: None; pure deterministic projection.
 * Idempotency: REQUIRED.
 */
function projectComplexVisitPreviewData(
  assembled: DocumentRenderData,
  complexVisit: PreviewDataSource['complexVisit'],
): DocumentRenderData {
  if (!complexVisit) return assembled;
  return {
    ...assembled,
    complexVisitSummary: complexVisit.summary,
    complexParticipants: complexVisit.participants.map((participant) => ({
      ...participant,
      data: { ...assembled },
    })),
    complexStandaloneSummary: complexVisit.standaloneSummary,
  };
}

/**
 * @process resolveDocumentPreviewData
 * Purpose:  Client-side equivalent of the worker's transformDocumentData(); turns a PreviewDataSource
 *           (Dexie / SAMPLE_PREVIEW_DATA) + DocumentConfig into the DocumentRenderData the shared
 *           renderer consumes. Performs only Dexie-shape extraction, then delegates assembly + gating
 *           to the shared core so preview and PDF stay in lockstep; projects complex-visit fields
 *           only when explicitly present on the source.
 * Steps:    1. Normalize merge context. 2. Map violations/photos and resolve signatures from the
 *           Dexie/sample shapes. 3. Resolve advanced-field bindings (preview source). 4. Derive the
 *           department branding. 5. Delegate to assembleDocumentRenderData. 6. For complexVisit scope,
 *           copy explicitly supplied complex-visit summary/participant data, including sample
 *           values only when SAMPLE_PREVIEW_DATA is the selected source.
 * Inputs:   dataSource (PreviewDataSource), docConfig (DocumentConfig), advancedBindings? (placements),
 *           documentLayout? (canvas band model — the gating source of truth when present),
 *           resolveUploadedFileUrl? (maps a template-specific uploaded logo id to a displayable URL).
 * Outputs:  DocumentRenderData.
 * Failure:  Tolerant — missing sub-objects fall back to neutral defaults; never throws on absent data.
 * Idempotency: REQUIRED — pure apart from the generated-at preview stamp.
 *
 * @localFirst
 * Source of Truth: local — operates on plain objects from Dexie or SAMPLE_PREVIEW_DATA. No API calls.
 */
export function resolveDocumentPreviewData(
  dataSource: PreviewDataSource,
  docConfig: DocumentConfig,
  advancedBindings?: AdvancedFieldPlacement[],
  documentLayout?: DocumentLayout,
  resolveUploadedFileUrl?: (uploadedFileId: string) => string | undefined,
): DocumentRenderData {
  const now = new Date();
  const reportNumber = dataSource.series._id?.toString() || 'PREVIEW';

  const mergeCtx: MergeFieldContext = {
    entity: dataSource.entity,
    series: dataSource.series,
    event: dataSource.event,
    inspector: dataSource.inspector,
    department: dataSource.department,
    reportNumber,
    generatedAt: now,
    violations: dataSource.violations,
  };

  // Advanced-field bindings (preview source). The shared assembler substitutes the {{advField:id}}
  // tokens into HTML body paragraphs and gates the advanced section.
  let advancedFields: ResolvedAdvancedField[] | undefined;
  if (advancedBindings && advancedBindings.length > 0 && dataSource.advanced) {
    advancedFields = resolveAllAdvancedBindings(
      advancedBindings,
      dataSource.advanced.responses,
      dataSource.advanced.templateSnapshot,
    ).fields;
  }

  const advBinding = (docConfig as { advancedBinding?: AdvancedBinding }).advancedBinding;

  // Finding 2: pass the department brand colors so a 'none' (Department Default) template renders the
  // same colors here as in the worker-generated PDF. Mirrors the worker's brandColors source and the
  // shared default exactly, so the two paths agree for every branding state.
  const brandColors = dataSource.department.branding?.brandColors as
    | { primary?: string; secondary?: string }
    | undefined;
  const branding = brandColors?.primary
    ? { primary: brandColors.primary, secondary: brandColors.secondary }
    : DEFAULT_DOCUMENT_BRANDING;

  // Finding 1: the canvas band model is the gating source of truth when present (the activation list
  // flips band.enabled); legacy section-only templates fall back to config.sections[].enabled inside
  // the shared computeEnabledSections.
  const layout = documentLayout ?? (docConfig as { documentLayout?: DocumentLayout }).documentLayout;

  const assembled = assembleDocumentRenderData({
    config: docConfig,
    mergeContext: mergeCtx,
    violationItems: mapViolationItems(dataSource.violations, docConfig),
    photos: mapPhotos(dataSource.photos, docConfig),
    signatures: resolveSignatures(docConfig, mergeCtx),
    documentLayout: layout,
    branding,
    resolveUploadedFileUrl,
    advancedFields,
    advancedBinding: advBinding
      ? {
          renderAdvancedSection: advBinding.renderAdvancedSection,
          advancedSectionLabel: advBinding.advancedSectionLabel,
        }
      : undefined,
  });
  return docConfig.reportDataScope === 'complexVisit'
    ? projectComplexVisitPreviewData(assembled, dataSource.complexVisit)
    : assembled;
}
